import { useEffect } from 'react';
import { scrollToSection } from '@/components/Ui/UiLink';

const SETTLE_MS = 2500;
const USER_EVENTS = ['wheel', 'touchstart', 'keydown'] as const;

/**
 * Brings the section with this id to the top and keeps it there while the
 * sections above it finish loading and grow; the first wheel, touch or key stops it.
 * Runs again whenever `id` or `trigger` changes; pass the location key to repeat it per navigation.
 */
export function useScrollToSection(id: string | null | undefined, trigger?: unknown) {
  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger only re-runs the effect
  useEffect(() => {
    if (!id) return;
    let done = false;
    const align = () => {
      if (!done) document.getElementById(id)?.scrollIntoView({ block: 'start' });
    };
    const observer = new ResizeObserver(align);
    const finish = () => {
      done = true;
      observer.disconnect();
      for (const e of USER_EVENTS) window.removeEventListener(e, finish);
    };
    const timer = setTimeout(() => {
      if (done) return;
      finish();
      // Settled: move focus to the section for keyboard and screen reader users.
      scrollToSection(id);
    }, SETTLE_MS);
    observer.observe(document.body);
    for (const e of USER_EVENTS) window.addEventListener(e, finish, { passive: true });
    align();
    return () => {
      clearTimeout(timer);
      finish();
    };
  }, [id, trigger]);
}
